import AdsContainer from "@components/Blocks/Container/AdsContainer";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@components/Modules/LoadMore/SetFirstRenderedArticleIds";
import { LOAD_MORE_CTA_COPY } from "@config/ctaCopy";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import fetchBHApi from "@helper/fetchBHApi";
import { displayAfter } from "@helper/utils";
import styles from "@pages/UGC/UGCLandingPage/UGCLandingPage.module.css";
import type { ProcessedArticleData } from "@transformer/useOSResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import cx from "classnames";
import { useState } from "react";

import { ArticleCardWrapper } from "./Component/ArticleCardWrapper";
import { MATA_MATA_SECTION } from "./UGCLandingPage";

const PAGE_SIZE = 4;

type UGCStoriesInfiniteListProps = {
  stories: ProcessedArticleData[];
};

export default function UGCStoriesInfiniteList({
  stories,
}: UGCStoriesInfiniteListProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);

  /**
   * Sets a precaution for duplicate article ids when rendering additional articles
   */
  useSetFirstRenderedArticleIds(stories, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? stories.at(-1) : loadMoreData.at(-1);

    const requestData: MoreArticlesRequestData = {
      sort: lastLoadedArticle?.sort,
    };

    const fetchUGCLandingPageMoreData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "4",
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `more-ugc-stories?${queryParams}`,
        "GET",
      );

      return response;
    };

    const createLoadMoreHandlerProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchUGCLandingPageMoreData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      lastLoadedArticle,
      loadMoreData,
      renderedArticleIds,
      pageSize: PAGE_SIZE,
      pageNumber,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreHandlerProps);
    return response;
  };

  return (
    <>
      {loadMoreData.map((context, index) => {
        if (index == 0 || displayAfter(index, PAGE_SIZE)) {
          return (
            <div key={`ads-${index}`} className="w-full">
              <AdsContainer
                className={cx("flex justify-center", styles.ugcAdsMainContent)}
              >
                <CompanionAdsLb
                  uniqueSectionName={MATA_MATA_SECTION}
                  index={index / PAGE_SIZE + 1}
                  stripeBackground={false}
                  pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
                />
              </AdsContainer>
            </div>
          );
        }

        if (index === 1 || (index > 2 && index % 2 === 1)) {
          return (
            <div key={index} className="flex w-full mb-6 lg:mb-8 gap-[22px]">
              <ArticleCardWrapper context={loadMoreData[index - 1]} />
              <ArticleCardWrapper context={context} />
            </div>
          );
        }
      })}
      {hasMoreStories ? (
        <div className="flex w-full justify-center">
          <LoadMore
            rootClassName="my-4 w-full"
            onLoadMore={handleLoadMore}
            hasMore={hasMoreStories}
            loadText={LOAD_MORE_CTA_COPY}
            virtualPVTitle={MATA_MATA_SECTION}
            pageNumber={pageNumber}
          />
        </div>
      ) : null}
    </>
  );
}
